import { FC } from 'react';

import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { formatDate } from 'utils/date';

import { Card, CardStyle, Text, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { BannerModerationInfo, LastModerationStatus } from 'store/models/moderation';
import { useSelector } from 'store/utils';

interface BannerLastModerationStatusProps {
    bannerId: number;
    isModeration: boolean | undefined;
    lastModeration: BannerModerationInfo['lastModeration'];
}

const BannerLastModerationStatusCardBackgrounds: Record<LastModerationStatus, CardStyle> = {
    [LastModerationStatus.NeedModeration]: 'warning-secondary',
    [LastModerationStatus.Approved]: 'positive-secondary',
    [LastModerationStatus.Rejected]: 'negative-secondary',
};

const TrlKeys = {
    title: {
        [LastModerationStatus.NeedModeration]: 'admin.moderation.banner.lastModerationStatus.title.needModeration',
        [LastModerationStatus.Approved]: 'admin.moderation.banner.lastModerationStatus.title.approved',
        [LastModerationStatus.Rejected]: 'admin.moderation.banner.lastModerationStatus.title.rejected',
    },
    date: 'admin.moderation.banner.lastModerationStatus.date',
    moderator: 'admin.moderation.banner.lastModerationStatus.moderator',
    rejectReasons: 'admin.moderation.banner.lastModerationStatus.rejectReasons',
    comment: 'admin.moderation.banner.lastModerationStatus.comment',
};

export const BannerLastModerationStatus: FC<BannerLastModerationStatusProps> = ({
    bannerId,
    isModeration,
    lastModeration,
}) => {
    const trls = useTrls();
    const moderationReasons = useSelector((state) => state.moderation.moderationReasons);

    if (!lastModeration) {
        return null;
    }

    const { comment, lastModerationTime, moderatorName, rejectReasons, moderationStatus } = lastModeration;

    if (isModeration && moderationStatus === LastModerationStatus.NeedModeration) {
        return null;
    }

    return (
        <Card
            borderRadius={16}
            padding={16}
            stretched
            style={BannerLastModerationStatusCardBackgrounds[moderationStatus]}
            data-qa={`moderation-banner-status-${bannerId}`}
        >
            <VSpacingContainer default={4}>
                <Text typography="custom-3-semibold">{trls(TrlKeys.title[moderationStatus])}</Text>
                {moderationStatus !== LastModerationStatus.NeedModeration && (
                    <>
                        <div>
                            <Text Element="span" typography="custom-3-semibold">
                                {trls(TrlKeys.date)}
                            </Text>
                            {NON_BREAKING_SPACE}
                            <Text Element="span" typography="label-3-regular">
                                {formatDate(lastModerationTime, 'HH:mm dd.MM.yyyy')}
                            </Text>
                        </div>
                        <div>
                            <Text Element="span" typography="custom-3-semibold">
                                {trls(TrlKeys.moderator)}
                            </Text>
                            {NON_BREAKING_SPACE}
                            <Text Element="span" typography="label-3-regular">
                                {moderatorName}
                            </Text>
                        </div>
                    </>
                )}
                {moderationStatus === LastModerationStatus.Rejected && (
                    <>
                        {rejectReasons.length > 0 && (
                            <div>
                                <Text Element="span" typography="custom-3-semibold">
                                    {trls(TrlKeys.rejectReasons)}
                                </Text>
                                {NON_BREAKING_SPACE}
                                <Text Element="span" typography="label-3-regular">
                                    {rejectReasons
                                        .map(
                                            (reasonId) =>
                                                moderationReasons.find((reason) => reason.id === reasonId)?.title
                                        )
                                        .join(', ')}
                                </Text>
                            </div>
                        )}
                        {comment && (
                            <div>
                                <Text Element="span" typography="custom-3-semibold">
                                    {trls(TrlKeys.comment)}
                                </Text>
                                {NON_BREAKING_SPACE}
                                <Text Element="span" typography="label-3-regular">
                                    {comment}
                                </Text>
                            </div>
                        )}
                    </>
                )}
            </VSpacingContainer>
        </Card>
    );
};
